/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * CartTimer.jsx
 * Created by Destin Frasier on 7/26/17.
 * Enhancements added by Betty Huang on 8/10/17.
 */

import * as React from 'react';
import cx from 'classnames';
import { differenceInMilliseconds, intervalToDuration, type Duration } from 'date-fns';

import { Icon, announce, pluralize } from 'sarsaparilla';

import styles from './CartTimer.module.scss';

type CartTimerProps = {
    date: Date;
    onComplete?: () => void;
    reservationCount?: number;
};

export function CartTimer({ date, onComplete, reservationCount = 0 }: CartTimerProps) {
    const [duration, setDuration] = React.useState<Duration>();
    const timerRef = React.useRef<number | undefined>();
    const tickRef = React.useRef<() => void>();

    const tick = () => {
        const remaining = -1 * differenceInMilliseconds(new Date(), date);
        const timeDuration = intervalToDuration({ start: new Date(), end: date });

        setDuration(timeDuration);

        if (remaining <= 0) {
            if (onComplete) {
                onComplete();
            }

            window.clearInterval(timerRef.current);
        }
    };

    /**
     * We do this to have access to the updated date in case the user extend
     * the time
     */
    tickRef.current = tick;

    const getAccessibleTime = () => {
        const hh = duration?.hours || 0;
        const mm = duration?.minutes || 0;
        const ss = duration?.seconds || 0;

        const hours = hh > 0 ? `${hh} ${pluralize(hh, 'hour', 'hours')}` : '';
        const minutes = mm > 0 ? `${mm} ${pluralize(mm, 'minute', 'minutes')}` : '';
        const seconds = ss > 0 ? `${ss} ${pluralize(ss, 'second', 'seconds')}` : '';

        return `Cart - ${hours} ${minutes} ${seconds} remaining to complete transactions`;
    };

    React.useEffect(() => {
        tick();

        timerRef.current = window.setInterval(() => {
            if (tickRef.current) {
                tickRef.current();
            }
        }, 1000);
        const announceTimerMessage = `You have ${getAccessibleTime()}`;

        announce(announceTimerMessage);

        return () => {
            window.clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatTime = (num: number) => {
        if (num <= 0) {
            return '00';
        }

        return num < 10 ? `0${num}` : num.toString();
    };

    const renderTime = () => {
        const hh = formatTime(duration?.hours || 0);
        const mm = formatTime(duration?.minutes || 0);
        const ss = formatTime(duration?.seconds || 0);

        let time = (
            <time>
                {hh}:{mm}:{ss}
            </time>
        );

        // Hide hours if the remaining time is shorter than 60 minutes
        if (hh === '00') {
            time = (
                <time>
                    {mm}:{ss}
                </time>
            );
        }

        return time;
    };

    return (
        <a href="/cart" className={styles.cartTimer}>
            <span className="rec-sr-only">{getAccessibleTime()}</span>
            <span className={styles.countdown} aria-hidden="true">
                <span className={styles.mobileCartIconWrap}>
                    <Icon
                        iconName="cart"
                        size="sm"
                        className={cx({
                            [styles.hasBadge]: reservationCount > 0,
                        })}
                    />
                    {reservationCount > 0 && (
                        <span className={styles.cartQuantity}>{reservationCount}</span>
                    )}
                </span>
                <Icon
                    iconName="hourglass-half"
                    size="sm"
                    className={styles.hourglassIcon}
                />
                {renderTime()}
            </span>
        </a>
    );
}
